import {
  Briefcase,
  Calendar,
  CheckCircle,
  CheckList,
  FileText,
  Helix,
  Home,
  PlayCircle,
  Sparkle,
  SparkleHeart,
  UserCheck,
  Users,
} from '@lifeomic/chromicons';
import urls from '../util/urls';
import { enforce, strictEnforce, permissions } from '../util/abac';
import _ from 'lodash';
import pwtUrls from '@lifeomic/phc-web-toolkit/src/util/urls';
import { getIsAccountFeatureDisabled } from '@lifeomic/phc-web-toolkit/src/hooks/useIsAccountFeatureDisabled';
import { AbacReducedPolicy } from '@lifeomic/abac';

export const testIds = {
  home: 'tile-home',
  subjectSearch: 'tile-subjectSearch',
  employees: 'tile-employees',
  files: 'tile-files',
  manageProjectConsent: 'tile-manageProjectConsent',
  manageSurveys: 'tile-manageSurveys',
  onboardingSnapshot: 'tile-onboardingSnapshot',
  fileValidation: 'tile-fileValidation',
  regenerativeMedicineOps: 'tile-regenerativeMedicineOps',
  reportTemplates: 'tile-reportTemplates',
  zoomAppointments: 'tile-zoomAppointments',
  aiRules: 'tile-aiRules',
  zoriChat: 'tile-zoriChat',
  genomics: 'tile-genomics',
  genomicsTests: 'tile-genomicsTests',
  genomicsIngestions: 'tile-genomicsIngestion',
  genomicsActions: 'tile-genomicsActions',
};

const status = {
  active: 'active',
};

const _safeEnforce = (
  operations: string | string[],
  policy: AbacReducedPolicy,
  attributes?: Record<string, unknown>,
) => {
  const operationEnforcer = attributes
    ? (operation: any) => strictEnforce(operation, policy, attributes)
    : (operation: any) => enforce(operation, policy);

  if (typeof operations === 'string') {
    operations = [operations];
  }

  return policy && !_.isEmpty(policy) && _.some(operations, operationEnforcer);
};

const accountNotDeactivated = (activeAccount: any) =>
  (activeAccount || {}).status !== 'DEACTIVATED';

const isFeatureEnabledAndAccountActive = (
  activeAccount: any,
  features: any,
  feature: string,
) =>
  !!activeAccount &&
  ((features || {})[feature] ||
    (activeAccount.features || []).includes(feature));

export const tiles = {
  home: {
    id: 'home',
    testId: testIds.home,
    title: 'Home',
    description: 'Home',
    intlId: 'tiles.home',
    uri: urls.app.home(),
    tileIcon: Home,
    requiresProject: false,
    status: status.active,
    shouldDisplay: () => true,
  },
  subjectSearch: {
    id: 'subjectsSearch',
    testId: testIds.subjectSearch,
    title: 'Members',
    description: '',
    intlId: 'manageProjects.primary.members',
    uri: urls.app.members(),
    tileIcon: Users,
    navIcon: Users,
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any, policy: any) =>
      !!activeAccount &&
      accountNotDeactivated(activeAccount) &&
      _safeEnforce([permissions.readMaskedData, permissions.readData], policy),
  },
  files: {
    id: 'files',
    testId: testIds.files,
    title: 'Files',
    description: '',
    intlId: 'manageProjects.primary.files',
    uri: urls.app.files(),
    tileIcon: Briefcase,
    navIcon: Briefcase,
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any, policy: any) =>
      !!activeAccount &&
      accountNotDeactivated(activeAccount) &&
      _safeEnforce(
        [
          permissions.downloadFile,
          permissions.createData,
          permissions.readData,
          permissions.fileAdmin,
        ],
        policy,
      ) &&
      !getIsAccountFeatureDisabled(activeAccount, 'files'),
  },
  manageProjectConsent: {
    id: 'manageProjectConsent',
    testId: testIds.manageProjectConsent,
    title: 'Consent',
    intlId: 'accountInfo.secondary.manageProjectConsent',
    uri: urls.app.consentManagement.project(),
    tileIcon: CheckCircle,
    navIcon: CheckCircle,
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any, policy: any, features: any) =>
      isFeatureEnabledAndAccountActive(activeAccount, features, 'consent') &&
      _safeEnforce(
        [permissions.readData, permissions.readMaskedData],
        policy,
      ) &&
      !getIsAccountFeatureDisabled(activeAccount, 'consent'),
  },
  manageSurveys: {
    id: 'manage-surveys',
    testId: testIds.manageSurveys,
    title: 'Surveys',
    description: '',
    intlId: 'tiles.manageSurveys',
    tileIcon: CheckList,
    uri: pwtUrls.app.manageSurveys(),
    navIcon: CheckList,
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any, policy: any, features: any) =>
      isFeatureEnabledAndAccountActive(activeAccount, features, 'surveys') &&
      _safeEnforce(
        [permissions.readData, permissions.readMaskedData],
        policy,
      ) &&
      !getIsAccountFeatureDisabled(activeAccount, 'surveys'),
  },
  onboardingSnapshot: {
    id: 'onboardingSnapshot',
    testId: testIds.onboardingSnapshot,
    title: 'Onboarding Snapshot',
    description: '',
    intlId: 'tiles.workflows',
    uri: urls.app.workflows.onboardingSnapshot(),
    tileIcon: PlayCircle,
    navIcon: PlayCircle,
    status: status.active,
    requiresProject: false,
    shouldDisplay: (activeAccount: any) =>
      !!activeAccount && activeAccount.id?.startsWith('fountainlife'),
  },
  fileValidation: {
    id: 'fileValidation',
    testId: testIds.fileValidation,
    title: 'File Validation',
    description: '',
    intlId: 'tiles.fileValidation',
    uri: urls.app.workflows.fileValidation(),
    tileIcon: FileText,
    navIcon: FileText,
    status: status.active,
    requiresProject: false,
    shouldDisplay: (activeAccount: any) =>
      !!activeAccount && activeAccount.id?.startsWith('fountainlife'),
  },
  regenerativeMedicineOps: {
    id: 'regenerativeMedicineOps',
    testId: testIds.regenerativeMedicineOps,
    title: 'Regenerative Medicine Ops',
    description: '',
    intlId: 'tiles.regenerativeMedicineOps',
    uri: urls.app.workflows.regenerativeMedicineOps(),
    tileIcon: SparkleHeart,
    navIcon: SparkleHeart,
    status: status.active,
    requiresProject: false,
    shouldDisplay: (activeAccount: any, _policy: any, features: any) =>
      !!activeAccount &&
      activeAccount.id?.startsWith('fountainlife') &&
      features?.regenerativeMedicineOps,
  },
  reportTemplates: {
    id: 'reportTemplates',
    testId: testIds.reportTemplates,
    title: 'Zori Templates',
    description: '',
    intlId: 'tiles.reportTemplates',
    uri: urls.app.workflows.reportTemplates(),
    tileIcon: FileText,
    navIcon: FileText,
    status: status.active,
    requiresProject: false,
    shouldDisplay: (activeAccount: any) =>
      !!activeAccount && activeAccount.id?.startsWith('fountainlife'),
  },
  aiRules: {
    id: 'aiRules',
    testId: testIds.aiRules,
    title: 'Zori AI',
    description: '',
    intlId: 'tiles.zoriRules',
    uri: urls.app.workflows.zoriRules(),
    tileIcon: Sparkle,
    navIcon: Sparkle,
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any) =>
      ['fountainlife', 'fountainlifetest'].includes(activeAccount?.id),
  },
  zoriChat: {
    id: 'zoriChat',
    testId: testIds.zoriChat,
    title: 'Zori Chat',
    description: '',
    intlId: 'tiles.zoriChat',
    uri: urls.app.workflows.zoriChat(),
    tileIcon: Sparkle,
    navIcon: Sparkle,
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any, _policy: any, features: any) =>
      isFeatureEnabledAndAccountActive(
        activeAccount,
        features,
        'clinicianAgentChat',
      ) ||
      isFeatureEnabledAndAccountActive(
        activeAccount,
        features,
        'clinicianAgentChatGlobal',
      ),
  },
  zoomAppointments: {
    id: 'zoomAppointments',
    testId: testIds.zoomAppointments,
    title: 'Zoom Appointments',
    description: '',
    intlId: 'tiles.zoomAppointments',
    uri: urls.app.workflows.zoomAppointments(),
    tileIcon: Calendar,
    navIcon: Calendar,
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any) =>
      ['fountainlife', 'fountainlifetest'].includes(activeAccount?.id),
  },
  genomics: {
    id: 'omics',
    testId: testIds.genomics,
    title: 'Omics',
    description: '',
    intlId: 'manageProjects.primary.genomics',
    uri: urls.app.omics.dashboard(),
    tileIcon: Helix,
    navIcon: Helix,
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any, policy: any) =>
      !!activeAccount &&
      accountNotDeactivated(activeAccount) &&
      _safeEnforce(
        [permissions.readData, permissions.readMaskedData],
        policy,
      ) &&
      !getIsAccountFeatureDisabled(activeAccount, 'genomics'),
  },
  genomicsTests: {
    id: 'omicsTests',
    testId: testIds.genomicsTests,
    title: 'Tests',
    description: '',
    intlId: 'manageProjects.primary.genomicTests',
    uri: urls.app.omics.tests(),
    tileIcon: Helix,
    navIcon: Helix,
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any, policy: any) =>
      !!activeAccount &&
      accountNotDeactivated(activeAccount) &&
      _safeEnforce(
        [permissions.readData, permissions.readMaskedData],
        policy,
      ) &&
      !getIsAccountFeatureDisabled(activeAccount, 'genomics'),
  },
  genomicsIngestions: {
    id: 'omicsIngestions',
    testId: testIds.genomicsIngestions,
    title: 'Ingestions',
    description: '',
    intlId: 'manageProjects.primary.genomicIngestions',
    uri: urls.app.omics.ingestions(),
    tileIcon: Helix,
    navIcon: Helix,
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any, policy: any) =>
      !!activeAccount &&
      accountNotDeactivated(activeAccount) &&
      _safeEnforce(permissions.createGenomicData, policy) &&
      !getIsAccountFeatureDisabled(activeAccount, 'genomics'),
  },
  genomicsActions: {
    id: 'omicsActions',
    testId: testIds.genomicsActions,
    title: 'Actions',
    description: '',
    intlId: 'manageProjects.primary.genomicsActions',
    uri: urls.app.omics.actions(),
    tileIcon: Helix,
    navIcon: Helix,
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any, policy: any) =>
      !!activeAccount &&
      accountNotDeactivated(activeAccount) &&
      _safeEnforce([permissions.fileAdmin], policy) &&
      _safeEnforce([permissions.createGenomicData], policy) &&
      !getIsAccountFeatureDisabled(activeAccount, 'genomics'),
  },
  employees: {
    id: 'employees',
    testId: testIds.employees,
    title: 'Employees',
    description: '',
    intlId: 'manageProjects.primary.employees',
    uri: urls.app.employees(),
    tileIcon: UserCheck,
    navIcon: UserCheck,
    status: status.active,
    requiresProject: true,
    shouldDisplay: (activeAccount: any, policy: any) =>
      !!activeAccount &&
      activeAccount.id?.startsWith('fountainlife') &&
      _safeEnforce(permissions.employeeAdmin, policy),
  },
};

const tileConfig = {
  manageProjects: {
    primary: [
      { tile: tiles.onboardingSnapshot },
      { tile: tiles.regenerativeMedicineOps },
      { tile: tiles.reportTemplates },
      { tile: tiles.zoomAppointments },
      { tile: tiles.aiRules },
      { tile: tiles.subjectSearch },
      { tile: tiles.manageProjectConsent },
      { tile: tiles.manageSurveys },
      { tile: tiles.files },
      { tile: tiles.genomics },
    ],
  },
};

export default tileConfig;
